// Account overview — KPIs + upcoming stays + quick actions.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, CalendarDays, ClipboardList, ArrowRight, MapPin } from "lucide-react";
import { customerApi, useCustomerAuth } from "@/lib/customerAuth";

const usd = (n) => `$${Number(n || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

export default function AccountOverview() {
    const { user } = useCustomerAuth();
    const [reservations, setReservations] = useState([]);

    useEffect(() => {
        customerApi.get("/customer/reservations").then((r) => setReservations(r.data)).catch(() => {});
    }, []);

    const today = new Date().toISOString().slice(0, 10);
    const upcoming = reservations.filter((r) => r.check_in >= today && !["cancelled", "no_show"].includes(r.status));
    const past = reservations.filter((r) => r.check_out < today || ["checked_out"].includes(r.status));

    return (
        <div className="space-y-8" data-testid="account-overview">
            {/* Summary cards */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card
                    testid="ov-loyalty"
                    icon={<Sparkles size={16} className="text-gold" />}
                    label="Cove points available"
                    value={user.loyalty?.available ?? 0}
                    hint={`${user.loyalty?.tier || "Cove Member"}${user.loyalty?.pending ? ` · ${user.loyalty.pending} pending` : ""}`}
                />
                <Card
                    testid="ov-upcoming"
                    icon={<CalendarDays size={16} className="text-jade" />}
                    label="Upcoming stays"
                    value={upcoming.length}
                    hint={upcoming.length ? `Next: ${upcoming[0].check_in}` : "Time for a getaway?"}
                />
                <Card
                    testid="ov-past"
                    icon={<ClipboardList size={16} className="text-navy" />}
                    label="Past stays"
                    value={past.length}
                    hint={`${reservations.length} total reservations`}
                />
            </section>

            {/* Upcoming reservations */}
            <section>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-2xl text-navy">Upcoming stays</h2>
                    {upcoming.length > 0 && <Link to="/account/bookings" className="text-[11px] uppercase tracking-[0.16em] text-navy/60 hover:text-gold">View all <ArrowRight size={11} className="inline" /></Link>}
                </div>
                {upcoming.length === 0 ? (
                    <div className="bg-white border border-navy/10 rounded-2xl p-8 text-center">
                        <MapPin size={22} className="mx-auto text-navy/30 mb-3" />
                        <p className="text-navy/60 text-sm">No upcoming stays yet.</p>
                        <Link to="/booking" className="mt-4 inline-flex items-center gap-2 bg-navy text-ivory px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.18em] hover:bg-navy/90">
                            Book a stay <ArrowRight size={12} />
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {upcoming.map((r) => (
                            <Link to={`/account/bookings/${r.id}`} key={r.id} data-testid={`ov-res-${r.id}`} className="block bg-white border border-navy/10 rounded-2xl p-5 hover:border-gold/50 transition-colors">
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{r.confirmation_number}</div>
                                        <div className="font-display text-xl text-navy mt-1">{r.room_name || r.room_slug}</div>
                                        <div className="text-navy/60 text-sm mt-1">{r.check_in} → {r.check_out} · {r.nights} nights</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-jade/10 text-jade inline-block">{r.status?.replace("_", " ")}</div>
                                        <div className="mt-2 font-display text-lg text-navy">{usd(r.total)}</div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* Quick actions */}
            <section>
                <h2 className="font-display text-2xl text-navy mb-4">Quick actions</h2>
                <div className="grid md:grid-cols-2 gap-3">
                    <QuickAction to="/booking" label="Book another stay" desc="Fresh dates, favourite room" testid="qa-book" />
                    <QuickAction to="/account/loyalty" label="View Cove Rewards" desc="Balance, tier progress and history" testid="qa-loyalty" />
                    <QuickAction to="/account/profile" label="Update profile" desc="Preferences, contact info, marketing" testid="qa-profile" />
                    <QuickAction to="/contact" label="Contact the resort" desc="Concierge team, +1 (876) 634-7439" testid="qa-contact" />
                </div>
            </section>
        </div>
    );
}

function Card({ icon, label, value, hint, testid }) {
    return (
        <div data-testid={testid} className="bg-white border border-navy/10 rounded-2xl p-6">
            <div className="flex items-center gap-2">
                {icon}
                <div className="text-[10px] uppercase tracking-[0.22em] text-navy/60">{label}</div>
            </div>
            <div className="mt-3 font-display text-4xl text-navy">{value}</div>
            {hint && <div className="text-[11px] text-navy/50 mt-1">{hint}</div>}
        </div>
    );
}

function QuickAction({ to, label, desc, testid }) {
    return (
        <Link to={to} data-testid={testid} className="group bg-white border border-navy/10 rounded-2xl p-5 flex items-start gap-4 hover:border-gold/50 transition-colors">
            <div className="flex-1">
                <div className="font-medium text-navy">{label}</div>
                <div className="text-[11px] text-navy/55 mt-0.5">{desc}</div>
            </div>
            <ArrowRight size={14} className="text-navy/30 group-hover:text-gold mt-1" />
        </Link>
    );
}
